import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck, Database, Trash2, FileWarning, BarChart3,
  Loader2, CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ToolCard({ title, description, icon: Icon, buttonLabel, onRun, destructive, result, loading, extraAction }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className="border-border/60 bg-card/80" data-testid={`tool-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-md ${destructive ? "bg-red-500/10" : "bg-primary/10"}`}>
              <Icon size={18} className={destructive ? "text-red-400" : "text-primary"} />
            </div>
            <div>
              <CardTitle className="text-base font-semibold">{title}</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {extraAction}
            <Button
              data-testid={`run-${title.toLowerCase().replace(/\s/g, "-")}`}
              size="sm"
              variant={destructive ? "destructive" : "default"}
              onClick={onRun}
              disabled={loading}
            >
              {loading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
              {loading ? "Running..." : buttonLabel}
            </Button>
          </div>
        </div>
      </CardHeader>
      {result && (
        <CardContent className="pt-0">
          <ResultDisplay result={result} expanded={expanded} onToggle={() => setExpanded(!expanded)} />
        </CardContent>
      )}
    </Card>
  );
}

function ResultDisplay({ result, expanded, onToggle }) {
  if (result.error) {
    return (
      <div className="p-3 rounded-md bg-red-500/10 border border-red-500/20">
        <div className="flex items-center gap-2 text-red-400 text-sm font-medium">
          <XCircle size={14} /> Error
        </div>
        <p className="text-xs text-red-300/80 mt-1 font-mono">{result.error}</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {/* Summary bar */}
      <div className={`p-3 rounded-md border ${result.healthy === false ? "bg-amber-500/10 border-amber-500/20" : "bg-emerald-500/10 border-emerald-500/20"}`}>
        <div className="flex items-center gap-2 text-sm font-medium">
          {result.healthy === false ? (
            <AlertTriangle size={14} className="text-amber-400" />
          ) : (
            <CheckCircle2 size={14} className="text-emerald-400" />
          )}
          <span className={result.healthy === false ? "text-amber-300" : "text-emerald-300"}>
            {result.summary}
          </span>
        </div>
      </div>

      {/* Detail data */}
      {result.details && result.details.length > 0 && (
        <div>
          <button onClick={onToggle} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {expanded ? "Collapse" : "Expand"} details ({result.details.length} items)
          </button>
          {expanded && (
            <div className="mt-2 max-h-80 overflow-y-auto space-y-1.5 scrollbar-thin">
              {result.details.map((item, i) => (
                <DetailRow key={i} item={item} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stats grid */}
      {result.stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
          {result.stats.map((s, i) => (
            <div key={i} className="p-2.5 bg-secondary/50 rounded-md">
              <p className="text-xs text-muted-foreground">{s.label}</p>
              <p className="text-lg font-semibold font-mono mt-0.5">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tables */}
      {result.tables && result.tables.map((table, ti) => (
        <div key={ti} className="mt-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">{table.title}</p>
          <div className="rounded-md border border-border overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-secondary/50">
                  {table.columns.map((col, ci) => (
                    <th key={ci} className="text-left p-2 font-medium text-muted-foreground">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, ri) => (
                  <tr key={ri} className="border-t border-border/40">
                    {row.map((cell, ci) => (
                      <td key={ci} className="p-2 text-foreground/90">{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function DetailRow({ item }) {
  const severityColor = {
    high: "bg-red-500/20 text-red-300 border-red-500/30",
    medium: "bg-amber-500/20 text-amber-300 border-amber-500/30",
    low: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  };
  return (
    <div className="p-2 rounded bg-secondary/30 border border-border/30 text-xs font-mono">
      <div className="flex items-center gap-2 mb-1">
        {item.severity && (
          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${severityColor[item.severity] || ""}`}>
            {item.severity}
          </Badge>
        )}
        <span className="text-muted-foreground">{item.type}</span>
      </div>
      <p className="text-foreground/80">{item.detail}</p>
      {item.question_text && <p className="text-muted-foreground mt-0.5 truncate">Q: {item.question_text}</p>}
    </div>
  );
}

export default function AdminTools() {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState({});
  const [flaggedIds, setFlaggedIds] = useState([]);
  const [removeLoading, setRemoveLoading] = useState(false);

  const setToolResult = (key, data) => setResults(prev => ({ ...prev, [key]: data }));
  const setToolLoading = (key, val) => setLoading(prev => ({ ...prev, [key]: val }));

  const runIntegrity = async () => {
    setToolLoading("integrity", true);
    setToolResult("integrity", null);
    setFlaggedIds([]);
    try {
      const { data } = await axios.get(`${API}/admin/verify-integrity`);
      const uniqueIds = [...new Set(data.issues.map(i => i.question_id))];
      setFlaggedIds(uniqueIds);
      setToolResult("integrity", {
        summary: data.healthy
          ? `All ${data.total_scanned} questions passed integrity checks`
          : `${data.issues_found} issue(s) found across ${uniqueIds.length} question(s) in ${data.total_scanned} total`,
        healthy: data.healthy,
        details: data.issues,
        stats: [
          { label: "Scanned", value: data.total_scanned },
          { label: "Issues", value: data.issues_found },
          { label: "Affected Questions", value: uniqueIds.length },
        ],
      });
    } catch (e) {
      setToolResult("integrity", { error: e.response?.data?.detail || e.message });
    }
    setToolLoading("integrity", false);
  };

  const removeFlagged = async () => {
    if (!flaggedIds.length) return;
    if (!window.confirm(`Remove ${flaggedIds.length} flagged question(s)? This cannot be undone.`)) return;
    setRemoveLoading(true);
    try {
      const { data } = await axios.delete(`${API}/admin/remove-questions`, { data: flaggedIds });
      setFlaggedIds([]);
      setToolResult("integrity", {
        summary: `Removed ${data.removed} question(s). ${data.total_after} remaining. Run scan again to verify.`,
        healthy: true,
        stats: [
          { label: "Removed", value: data.removed },
          { label: "Remaining", value: data.total_after },
        ],
        details: [],
      });
    } catch (e) {
      setToolResult("integrity", prev => ({
        ...results.integrity,
        error: e.response?.data?.detail || e.message,
      }));
    }
    setRemoveLoading(false);
  };

  const runStats = async () => {
    setToolLoading("stats", true);
    setToolResult("stats", null);
    try {
      const { data } = await axios.get(`${API}/admin/db-stats`);
      const t = data.totals;
      setToolResult("stats", {
        summary: `${t.questions} questions, ${t.pyq_questions} PYQ, ${t.tests} tests, ${t.test_attempts} attempts`,
        healthy: true,
        stats: [
          { label: "Questions", value: t.questions },
          { label: "PYQ Questions", value: t.pyq_questions },
          { label: "Tests Created", value: t.tests },
          { label: "Test Attempts", value: t.test_attempts },
          { label: "Used in Tests", value: data.questions_used_in_tests },
          { label: "Unused", value: data.questions_unused },
          { label: "Reported", value: t.reported },
          { label: "Feedback", value: t.feedback },
        ],
        tables: [
          {
            title: "Difficulty Distribution",
            columns: ["Difficulty", "Count"],
            rows: Object.entries(data.difficulty_distribution).sort((a, b) => b[1] - a[1]).map(([k, v]) => [k || "unset", v]),
          },
          {
            title: "Topic Distribution",
            columns: ["Topic", "Count"],
            rows: data.topic_distribution.map(t => [t.topic_name, t.count]),
          },
          {
            title: "Source Distribution",
            columns: ["Source", "Count"],
            rows: data.source_distribution.map(s => [s.source || "Unknown", s.count]),
          },
          ...(Object.keys(data.reported_status).length > 0 ? [{
            title: "Reported Questions Status",
            columns: ["Status", "Count"],
            rows: Object.entries(data.reported_status).map(([k, v]) => [k, v]),
          }] : []),
        ],
      });
    } catch (e) {
      setToolResult("stats", { error: e.response?.data?.detail || e.message });
    }
    setToolLoading("stats", false);
  };

  const runDedupe = async () => {
    setToolLoading("dedupe", true);
    setToolResult("dedupe", null);
    try {
      const { data } = await axios.get(`${API}/admin/dedupe-questions`);
      setToolResult("dedupe", {
        summary: data.removed > 0
          ? `Removed ${data.removed} duplicate(s). ${data.total_after} questions remaining.`
          : `No duplicates found. ${data.total_after} questions in bank.`,
        healthy: data.removed === 0,
        stats: [
          { label: "Removed", value: data.removed },
          { label: "Total After", value: data.total_after },
        ],
      });
    } catch (e) {
      setToolResult("dedupe", { error: e.response?.data?.detail || e.message });
    }
    setToolLoading("dedupe", false);
  };

  const runReported = async () => {
    setToolLoading("reported", true);
    setToolResult("reported", null);
    try {
      const { data } = await axios.get(`${API}/questions/reported`);
      const reports = Array.isArray(data) ? data : [];
      setToolResult("reported", {
        summary: reports.length > 0
          ? `${reports.length} reported question(s) found`
          : "No reported questions",
        healthy: reports.length === 0,
        details: reports.map(r => ({
          type: r.report_reason || "unknown",
          severity: r.status === "resolved" ? "low" : "high",
          detail: `${r.report_reason}${r.user_comment ? ": " + r.user_comment : ""}`,
          question_text: r.question_id,
        })),
        stats: [
          { label: "Total Reports", value: reports.length },
          { label: "Pending", value: reports.filter(r => r.status !== "resolved").length },
          { label: "Resolved", value: reports.filter(r => r.status === "resolved").length },
        ],
      });
    } catch (e) {
      setToolResult("reported", { error: e.response?.data?.detail || e.message });
    }
    setToolLoading("reported", false);
  };

  const runCorrectionReport = async () => {
    setToolLoading("correction", true);
    setToolResult("correction", null);
    try {
      const { data } = await axios.get(`${API}/correction-report`);
      setToolResult("correction", {
        summary: data.has_report ? "Correction report available" : "No correction report found",
        healthy: true,
        details: data.has_report ? [{ type: "report", detail: data.report }] : [],
      });
    } catch (e) {
      setToolResult("correction", { error: e.response?.data?.detail || e.message });
    }
    setToolLoading("correction", false);
  };

  const tools = [
    {
      key: "stats",
      title: "Database Stats",
      description: "View comprehensive question bank statistics — totals, difficulty, topic and source distributions",
      icon: BarChart3,
      buttonLabel: "Fetch Stats",
      onRun: runStats,
    },
    {
      key: "integrity",
      title: "Verify Integrity",
      description: "Scan all questions for truncated options, empty fields, missing keys, merged options",
      icon: ShieldCheck,
      buttonLabel: "Run Scan",
      onRun: runIntegrity,
      extraAction: flaggedIds.length > 0 ? (
        <Button
          data-testid="remove-flagged-btn"
          size="sm"
          variant="destructive"
          onClick={removeFlagged}
          disabled={removeLoading}
        >
          {removeLoading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Trash2 size={14} className="mr-1.5" />}
          {removeLoading ? "Removing..." : `Remove ${flaggedIds.length} Flagged`}
        </Button>
      ) : null,
    },
    {
      key: "dedupe",
      title: "Deduplicate Questions",
      description: "Find and remove duplicate questions (keeps earliest entry)",
      icon: Database,
      buttonLabel: "Run Dedupe",
      onRun: runDedupe,
      destructive: true,
    },
    {
      key: "reported",
      title: "Reported Questions",
      description: "View all user-reported incorrect questions and their status",
      icon: FileWarning,
      buttonLabel: "Fetch Reports",
      onRun: runReported,
    },
    {
      key: "correction",
      title: "Correction Report",
      description: "View the detailed answer correction audit trail",
      icon: Trash2,
      buttonLabel: "Fetch Report",
      onRun: runCorrectionReport,
    },
  ];

  return (
    <div className="p-8 lg:p-12 space-y-6 animate-fade-in" data-testid="admin-tools-page">
      <div>
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
          Admin Tools
        </h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Database utilities, integrity checks, and maintenance scripts — all in one place.
        </p>
      </div>

      <div className="space-y-4">
        {tools.map(tool => (
          <ToolCard
            key={tool.key}
            {...tool}
            result={results[tool.key]}
            loading={loading[tool.key] || false}
          />
        ))}
      </div>
    </div>
  );
}
